import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { object, string, date, number } from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  useTheme,
  InputAdornment
} from "@mui/material";
import {
  FileText,
  DollarSign,
  Tag
} from 'lucide-react';
import {
  formatDateTimeForInput,
  formatDateTimeForBackend,
} from "../../utils/functions/Helpers";
import Constants from "../../utils/constants/Constants";
import { createInvoice } from "./InvoiceService";
import { ToastContainer } from "react-toastify";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";

const invoiceSchema = object().shape({
  id: number().notRequired(),
  invoiceNumber: string().notRequired(),
  issuedDate: date().required("Issued Date is required."),
  dueDate: date().required("Due Date is required."),
  status: string().required("Status is required."),
  amountDue: number().required("Amount Due is required."),
  totalAmountPaid: number().required("Amount Paid is required."),
});

const buildInvoiceFormValues = (invoiceToEdit, reservation) => ({
  id: invoiceToEdit?.id ?? null,
  invoiceNumber: invoiceToEdit?.invoiceNumber ?? `INV-000${reservation?.id ?? ""}`,
  issuedDate: invoiceToEdit?.issuedDate
    ? formatDateTimeForInput(invoiceToEdit.issuedDate)
    : formatDateTimeForInput(new Date()),
  dueDate: invoiceToEdit?.dueDate
    ? formatDateTimeForInput(invoiceToEdit.dueDate)
    : formatDateTimeForInput(new Date()),
  status: invoiceToEdit?.status ?? Constants.INVOICE_STATUS.DUE,
  amountDue: invoiceToEdit?.amountDue ?? reservation?.totalPrice ?? 0,
  totalAmountPaid: invoiceToEdit?.totalAmountPaid ?? 0,
});

const InvoiceForm = (props) => {
  const {
    invoiceToEdit,
    isUpdating = false,
    reservation,
    setState,
    toggleModal,
    updateInvoice,
    user,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const setApiError = () => {};
  const statuses = Object.values(Constants.INVOICE_STATUS);

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(invoiceSchema),
    defaultValues: buildInvoiceFormValues(invoiceToEdit, reservation),
  });

  const setIsLoading = useLoadingSpinner();

  useEffect(() => {
    reset(buildInvoiceFormValues(invoiceToEdit, reservation));
  }, [invoiceToEdit, reservation, reset]);

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal !== undefined && toggleModal(false);
  };

  const onSubmit = async (data) => {
    data.issuedDate = formatDateTimeForBackend(data.issuedDate);
    data.dueDate = formatDateTimeForBackend(data.dueDate);

    const reservation_ = isUpdating ? invoiceToEdit?.reservation : reservation;
    const user_ = isUpdating ? invoiceToEdit?.user : user;

    data.reservation = reservation_ ?? null;
    data.user = user_ ?? null;
    data.totalAmountPaid = Number(data.totalAmountPaid ?? 0);
    data.amountDue = Number(data.amountDue ?? 0);

    setIsLoading(true);

    try {
      if (isUpdating) {
        await updateInvoice(data, setApiError, setState, setIsLoading);
      } else {
        await createInvoice(data, setApiError, setState, setIsLoading);
      }

      reset();
      toggleModal !== undefined && toggleModal(false);
    } catch {
      // Error feedback is handled in the service layer.
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <FileText size={28} color={colors.greenAccent[500]} />
              <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
                {isUpdating ? "Manage Invoice" : "Generate Invoice"}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 2 }}>
              {isUpdating ? 'Update the financial status of this transaction.' : 'Create a new billing record for this reservation.'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Invoice Number"
              {...register("invoiceNumber")}
              error={!!errors.invoiceNumber}
              helperText={errors.invoiceNumber?.message}
              variant="outlined"
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tag size={18} color={colors.grey[400]} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Issued Date"
              type="datetime-local"
              {...register("issuedDate")}
              error={!!errors.issuedDate}
              helperText={errors.issuedDate?.message}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="datetime-local"
              {...register("dueDate")}
              error={!!errors.dueDate}
              helperText={errors.dueDate?.message}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Amount Due"
              type="number"
              {...register("amountDue")}
              error={!!errors.amountDue}
              helperText={errors.amountDue?.message}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={18} color={colors.grey[400]} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Amount Paid"
              type="number"
              {...register("totalAmountPaid")}
              error={!!errors.totalAmountPaid}
              helperText={errors.totalAmountPaid?.message}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={18} color={colors.grey[400]} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel id="status-label" sx={{ color: colors.grey[300], "&.Mui-focused": { color: colors.greenAccent[500] } }}>Invoice Status</InputLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="status-label"
                    label="Invoice Status"
                    {...field}
                    sx={{
                      borderRadius: "12px",
                      color: colors.grey[100],
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                      "& .MuiSvgIcon-root": { color: colors.grey[100] }
                    }}
                  >
                    {statuses.map((statusItem) => (
                      <MenuItem key={statusItem} value={statusItem} sx={{ color: colors.grey[100] }}>
                        {statusItem}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.status?.message}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isUpdating ? 'Update Invoice' : 'Generate Invoice'}
            </Button>
          </Grid>
        </Grid>
      </form>
      <ToastContainer />
    </Box>
  );
};

InvoiceForm.propTypes = {
  invoiceToEdit: PropTypes.object,
  isUpdating: PropTypes.bool,
  reservation: PropTypes.object,
  setState: PropTypes.func,
  toggleModal: PropTypes.func,
  updateInvoice: PropTypes.func,
  user: PropTypes.object,
};

export default InvoiceForm;
